import { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import Breadcrumb from '@/components/feature/Breadcrumb';
import { articles } from '@/mocks/data';

export default function ArticleDetail() {
  const { slug } = useParams<{ slug: string }>();

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [slug]);

  const article = articles.find((a) => a.slug === slug);
  const relatedArticles = articles.filter((a) => a.slug !== slug).slice(0, 3);

  if (!article) {
    return (
      <Layout>
        <div className="w-full px-4 md:px-6 py-20 text-center">
          <h1 className="text-2xl font-bold text-foreground-950 mb-4">文章未找到</h1>
          <Link to="/articles" className="text-primary-600 hover:text-primary-700 cursor-pointer">
            返回攻略资讯
          </Link>
        </div>
      </Layout>
    );
  }

  const articleSchema = {
    '@context': 'https://schema.org',
    '@type': 'Article',
    headline: article.title,
    description: article.excerpt,
    datePublished: article.date,
    author: { '@type': 'Organization', name: 'PG国际下载008' },
    publisher: { '@type': 'Organization', name: 'PG国际下载008' },
  };

  return (
    <Layout>
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(articleSchema) }}
      />
      <Breadcrumb
        items={[
          { label: '首页', path: '/' },
          { label: '攻略资讯', path: '/articles' },
          { label: article.title },
        ]}
      />
      <article className="w-full px-4 md:px-6 py-8 md:py-12">
        <div className="max-w-4xl mx-auto">
          <div className="mb-8">
            <div className="flex items-center gap-3 mb-4 flex-wrap">
              <span className="px-3 py-1 rounded-full bg-primary-100/80 text-primary-700 text-xs font-medium">
                {article.category}
              </span>
              <span className="text-xs text-foreground-400">{article.date}</span>
              <span className="text-xs text-foreground-400 flex items-center gap-1">
                <i className="ri-eye-line"></i>
                {article.views} 阅读
              </span>
            </div>
            <h1 className="text-2xl md:text-3xl font-bold text-foreground-950 font-heading mb-4 leading-tight">
              {article.title}
            </h1>
            <div className="flex items-center gap-2 flex-wrap">
              {article.tags.map((tag) => (
                <span key={tag} className="px-2.5 py-1 rounded-full bg-background-100 text-foreground-500 text-xs">
                  #{tag}
                </span>
              ))}
            </div>
          </div>

          <div className="mb-8 rounded-xl overflow-hidden">
            <img
              src={article.image}
              alt={article.title}
              title={article.title}
              className="w-full h-auto object-cover rounded-xl"
            />
          </div>

          <div className="prose prose-sm md:prose-base max-w-none text-foreground-700 leading-relaxed">
            {article.content.split('\n').map((paragraph, index) => {
              const trimmed = paragraph.trim();
              if (!trimmed) return <br key={index} />;
              if (trimmed.startsWith('## ')) {
                return (
                  <h2 key={index} className="text-xl font-semibold text-foreground-950 font-heading mt-8 mb-4">
                    {trimmed.replace('## ', '')}
                  </h2>
                );
              }
              if (trimmed.startsWith('- ')) {
                return (
                  <li key={index} className="text-sm text-foreground-600 ml-5 mb-1 list-disc">
                    {trimmed.replace('- ', '')}
                  </li>
                );
              }
              if (/^\d+\./.test(trimmed)) {
                return (
                  <p key={index} className="text-sm text-foreground-700 mb-3 font-medium">
                    {trimmed}
                  </p>
                );
              }
              return (
                <p key={index} className="text-sm text-foreground-600 mb-4 leading-relaxed">
                  {trimmed}
                </p>
              );
            })}
          </div>

          <div className="mt-12 pt-8 border-t border-background-200/70">
            <h3 className="text-lg font-semibold text-foreground-950 font-heading mb-5">相关文章</h3>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
              {relatedArticles.map((ra) => (
                <Link
                  key={ra.id}
                  to={`/article/${ra.slug}`}
                  className="group rounded-xl overflow-hidden bg-background-50 border border-background-200/70 hover:border-primary-200/60 transition-all duration-300 cursor-pointer"
                >
                  <div className="h-40 overflow-hidden">
                    <img
                      src={ra.image}
                      alt={ra.title}
                      title={ra.title}
                      className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                    />
                  </div>
                  <div className="p-4">
                    <h4 className="text-sm font-medium text-foreground-950 line-clamp-2 group-hover:text-primary-600 transition-colors">
                      {ra.title}
                    </h4>
                    <span className="text-xs text-foreground-400 mt-2 block">{ra.date}</span>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        </div>
      </article>
    </Layout>
  );
}